import React, { useMemo, useState } from 'react';
import { api } from '../api';
import { computeRoomCosts } from '../computeSOWCosts';
import SOWCostTable from './SOWCostTable';

const ROOM_TYPE_LABELS = {
  master_bedroom: 'Master Bedroom',
  bedroom:        'Bedroom',
  master_bath:    'Master Bath',
  bathroom:       'Bathroom',
  kitchen:        'Kitchen',
  living:         'Living Room',
  dining:         'Dining Room',
  laundry:        'Laundry',
  hallway:        'Hallway',
  garage:         'Garage',
  other:          'Other',
};

const ROOM_TYPE_COLORS = {
  master_bedroom: '#2493b5',
  bedroom:        '#5bafd6',
  master_bath:    '#39b54a',
  bathroom:       '#6cc97a',
  kitchen:        '#e67e22',
  living:         '#8e44ad',
  dining:         '#8e44ad',
  laundry:        '#939598',
  hallway:        '#939598',
  garage:         '#939598',
  other:          '#939598',
};

const SELECT = ({ value, onChange, options, style }) => (
  <select value={value || ''} onChange={e => onChange(e.target.value)} style={style}>
    {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);

const CONDITION_3 = [['keep','Keep'],['repair','Repair'],['replace','Replace']];
const CONDITION_2 = [['keep','Keep'],['replace','Replace']];
const APPLIANCE_3 = [['reuse','Reuse'],['replace','Replace'],['new','New']];
const DOOR_CONDITIONS = [
  ['keep',                 'Keep'],
  ['keep_hardware',        'Keep + Hardware Change'],
  ['keep_paint_hardware',  'Keep, Paint + Hardware Change'],
  ['replace_hardware',     'Replace + Hardware'],
];

function Field({ label, children }) {
  return (
    <div style={{marginBottom:10}}>
      <label style={{fontSize:11,fontWeight:600,color:'var(--text-muted)',display:'block',marginBottom:3}}>{label}</label>
      {children}
    </div>
  );
}

function NumberInput({ value, onChange, placeholder, style }) {
  return (
    <input type="number" defaultValue={value ?? ''} placeholder={placeholder || '0'}
      onBlur={e => onChange(e.target.value === '' ? null : parseFloat(e.target.value))}
      style={{ width: '100%', ...style }} />
  );
}

function CheckBox({ label, checked, onChange }) {
  return (
    <label style={{display:'flex',alignItems:'center',gap:6,cursor:'pointer',fontSize:13}}>
      <input type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked ? 1 : 0)}
        style={{width:15,height:15,accentColor:'var(--brand)'}} />
      {label}
    </label>
  );
}

export default function RoomCard({ room, dealId, sowCosts, onUpdate, onDelete, statusLabel, statusColor }) {
  const [expanded, setExpanded] = useState(false);
  const [data, setData] = useState(room);

  const costLines = useMemo(() => {
    const lines = computeRoomCosts(data);
    return lines.map(l => ({ section: `room_${room.id}`, label: l.label, estimated: l.amount }));
  }, [data, room.id]);

  const save = async (field, value) => {
    const updated = { ...data, [field]: value };
    setData(updated);
    // Auto-calc sq_ft when both L and W are set
    if ((field === 'length_ft' || field === 'width_ft')) {
      const l = field === 'length_ft' ? value : updated.length_ft;
      const w = field === 'width_ft'  ? value : updated.width_ft;
      if (l && w) {
        const sf = parseFloat((l * w).toFixed(1));
        setData(d => ({...d, sq_ft: sf}));
        await api.updateRoom(dealId, room.id, { [field]: value, sq_ft: sf });
        return;
      }
    }
    if (field === 'closet_length' || field === 'closet_width') {
      const cl = field === 'closet_length' ? value : updated.closet_length;
      const cw = field === 'closet_width'  ? value : updated.closet_width;
      if (cl && cw) {
        const csf = parseFloat((cl * cw).toFixed(1));
        setData(d => ({...d, closet_sq_ft: csf}));
        await api.updateRoom(dealId, room.id, { [field]: value, closet_sq_ft: csf });
        return;
      }
    }
    await api.updateRoom(dealId, room.id, { [field]: value });
  };

  const isKitchen = ['kitchen'].includes(data.room_type);
  const isBath    = ['bathroom','master_bath'].includes(data.room_type);
  const hasFan    = !['hallway','laundry','bathroom','master_bath'].includes(data.room_type);
  const typeColor = ROOM_TYPE_COLORS[data.room_type] || '#939598';

  const sqFtDisplay = data.sq_ft
    ? `${data.sq_ft} sq ft`
    : (data.length_ft && data.width_ft ? `${data.length_ft}×${data.width_ft}` : '—');

  return (
    <div className="card" style={{marginBottom:12,padding:0,overflow:'hidden'}}>
      {/* Header */}
      <div
        style={{display:'flex',alignItems:'center',gap:10,padding:'12px 16px',cursor:'pointer',
          borderLeft:`4px solid ${typeColor}`}}
        onClick={() => setExpanded(e => !e)}
      >
        <div style={{flex:1}}>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <span style={{fontWeight:600,fontSize:14}}>{data.name}</span>
            <span style={{fontSize:11,background:`${typeColor}22`,color:typeColor,
              padding:'1px 7px',borderRadius:20,fontWeight:600}}>
              {ROOM_TYPE_LABELS[data.room_type] || data.room_type}
            </span>
          </div>
          <div style={{fontSize:12,color:'var(--text-muted)',marginTop:2}}>
            {sqFtDisplay}
            {data.has_closet ? ' · Closet' : ''}
            {data.ceiling_height ? ` · ${data.ceiling_height}′ ceiling` : ''}
          </div>
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          {costLines.length > 0 && (
            <span style={{fontSize:11,fontWeight:700,color:'var(--brand)',
              background:'var(--brand-light)',padding:'2px 8px',borderRadius:20}}>
              ${costLines.reduce((s,l) => s + l.estimated, 0).toLocaleString()}
            </span>
          )}
          {statusLabel && (
            <span style={{fontSize:11,fontWeight:600,padding:'2px 8px',borderRadius:12,
              background:`${statusColor}22`,color:statusColor,whiteSpace:'nowrap'}}>
              {statusLabel}
            </span>
          )}
          <button className="btn-ghost" style={{fontSize:11,color:'var(--danger)',padding:'3px 8px'}}
            onClick={e => { e.stopPropagation(); onDelete(room.id); }}>Delete</button>
          <span style={{color:'var(--text-muted)',fontSize:16}}>{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {expanded && (
        <div style={{padding:'16px',borderTop:'1px solid var(--bg)'}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0 20px'}}>

            {/* ── Dimensions ── */}
            <div style={{gridColumn:'span 2',marginBottom:4}}>
              <div className="section-title">Dimensions</div>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8,gridColumn:'span 2',marginBottom:12}}>
              <Field label="Length (ft)">
                <NumberInput value={data.length_ft} onChange={v => save('length_ft', v)} />
              </Field>
              <Field label="Width (ft)">
                <NumberInput value={data.width_ft} onChange={v => save('width_ft', v)} />
              </Field>
              <Field label="Sq Ft">
                <input type="number" value={data.sq_ft ?? ''} placeholder="auto"
                  onChange={e => setData(d => ({...d, sq_ft: e.target.value}))}
                  onBlur={e => save('sq_ft', e.target.value === '' ? null : parseFloat(e.target.value))} />
              </Field>
              <Field label="Ceiling Height (ft)">
                <NumberInput value={data.ceiling_height} onChange={v => save('ceiling_height', v)} placeholder="8" />
              </Field>
            </div>

            {/* ── Closet ── */}
            <div style={{gridColumn:'span 2',marginBottom:8}}>
              <CheckBox label="Has Closet" checked={data.has_closet} onChange={v => save('has_closet', v)} />
            </div>
            {!!data.has_closet && (
              <div style={{gridColumn:'span 2',display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8,
                background:'var(--bg)',borderRadius:'var(--radius)',padding:'10px 12px',marginBottom:12}}>
                <Field label="Closet Length (ft)">
                  <NumberInput value={data.closet_length} onChange={v => save('closet_length', v)} />
                </Field>
                <Field label="Closet Width (ft)">
                  <NumberInput value={data.closet_width} onChange={v => save('closet_width', v)} />
                </Field>
                <Field label="Closet Sq Ft">
                  <input type="number" value={data.closet_sq_ft ?? ''} placeholder="auto"
                    onChange={e => setData(d => ({...d, closet_sq_ft: e.target.value}))}
                    onBlur={e => save('closet_sq_ft', e.target.value === '' ? null : parseFloat(e.target.value))} />
                </Field>
              </div>
            )}

            {/* ── Flooring ── */}
            <div style={{gridColumn:'span 2',marginBottom:4}}><div className="section-title">Finishes</div></div>
            <Field label="Flooring Type">
              <SELECT value={data.flooring_type} onChange={v => save('flooring_type', v)}
                options={[['LVP','LVP'],['carpet','Carpet'],['tile','Tile'],['hardwood','Hardwood'],['none','None']]} />
            </Field>
            <Field label="Flooring Condition">
              <SELECT value={data.flooring_condition} onChange={v => save('flooring_condition', v)} options={CONDITION_3} />
            </Field>
            <Field label="Walls — Paint Needed">
              <SELECT value={data.paint_walls ? 'yes' : 'no'} onChange={v => save('paint_walls', v === 'yes' ? 1 : 0)}
                options={[['yes','Yes'],['no','No']]} />
            </Field>
            <Field label="Drywall Damage">
              <SELECT value={data.drywall_damage} onChange={v => save('drywall_damage', v)}
                options={[['none','None'],['minor','Minor'],['major','Major']]} />
            </Field>
            <Field label="Ceiling — Paint Needed">
              <SELECT value={data.paint_ceiling ? 'yes' : 'no'} onChange={v => save('paint_ceiling', v === 'yes' ? 1 : 0)}
                options={[['yes','Yes'],['no','No']]} />
            </Field>
            <Field label="Ceiling Texture">
              <SELECT value={data.texture_ceiling ? 'yes' : 'no'} onChange={v => save('texture_ceiling', v === 'yes' ? 1 : 0)}
                options={[['no','No'],['yes','Yes']]} />
            </Field>
            <Field label="Trim / Baseboards">
              <SELECT value={data.trim_condition} onChange={v => save('trim_condition', v)} options={CONDITION_3} />
            </Field>

            {/* ── Electrical ── */}
            <div style={{gridColumn:'span 2',marginBottom:4,marginTop:8}}><div className="section-title">Electrical</div></div>
            <Field label="Outlets">
              <NumberInput value={data.outlets} onChange={v => save('outlets', v)} />
            </Field>
            <Field label="Switches">
              <NumberInput value={data.switches} onChange={v => save('switches', v)} />
            </Field>
            <Field label="Light Fixtures">
              <NumberInput value={data.light_fixtures} onChange={v => save('light_fixtures', v)} />
            </Field>
            {hasFan && (
              <Field label="Ceiling Fans">
                <NumberInput value={data.ceiling_fans} onChange={v => save('ceiling_fans', v)} />
              </Field>
            )}

            {/* ── Doors ── */}
            <div style={{gridColumn:'span 2',marginBottom:4,marginTop:8}}><div className="section-title">Doors</div></div>
            <Field label="Interior Door Count">
              <NumberInput value={data.door_count} onChange={v => save('door_count', v)} />
            </Field>
            <Field label="Door Condition">
              <SELECT value={data.door_condition} onChange={v => save('door_condition', v)} options={DOOR_CONDITIONS} />
            </Field>
            {!!data.has_closet && (<>
              <Field label="Closet Door Count">
                <NumberInput value={data.closet_door_count} onChange={v => save('closet_door_count', v)} />
              </Field>
              <Field label="Closet Door Condition">
                <SELECT value={data.closet_door_condition} onChange={v => save('closet_door_condition', v)} options={DOOR_CONDITIONS} />
              </Field>
            </>)}

            {/* ── AC Vents ── */}
            <div style={{gridColumn:'span 2',marginBottom:4,marginTop:8}}><div className="section-title">AC Vents</div></div>
            <Field label="Vent Count">
              <NumberInput value={data.ac_vents} onChange={v => save('ac_vents', v)} placeholder="0" />
            </Field>
            <Field label="Vent Condition">
              <SELECT value={data.ac_vent_condition} onChange={v => save('ac_vent_condition', v)}
                options={[['keep','Keep'],['replace','Replace']]} />
            </Field>

            {/* ── Kitchen extras ── */}
            {isKitchen && (<>
              <div style={{gridColumn:'span 2',marginBottom:4,marginTop:8}}><div className="section-title">Kitchen</div></div>
              <Field label="Cabinets">
                <SELECT value={data.cabinet_condition} onChange={v => save('cabinet_condition', v)}
                  options={[['keep','Keep'],['paint','Paint'],['reface','Reface'],['replace','Replace']]} />
              </Field>
              <Field label="Cabinet Linear Ft">
                <NumberInput value={data.cabinet_linear_ft} onChange={v => save('cabinet_linear_ft', v)} />
              </Field>
              <Field label="Countertop Type">
                <SELECT value={data.countertop_type} onChange={v => save('countertop_type', v)}
                  options={[['','— Select —'],['laminate','Laminate'],['granite','Granite'],['quartz','Quartz'],
                    ['tile','Tile'],['solid_surface','Solid Surface'],['butcher_block','Butcher Block'],
                    ['marble','Marble'],['concrete','Concrete'],['other','Other']]} />
              </Field>
              <Field label="Countertop Sq Ft">
                <NumberInput value={data.countertop_sq_ft} onChange={v => save('countertop_sq_ft', v)} />
              </Field>
              <Field label="Backsplash Sq Ft">
                <NumberInput value={data.backsplash_sq_ft} onChange={v => save('backsplash_sq_ft', v)} />
              </Field>
              <Field label="Sink">
                <SELECT value={data.sink_condition} onChange={v => save('sink_condition', v)} options={CONDITION_2} />
              </Field>
              <div style={{gridColumn:'span 2'}}>
                <div className="section-title" style={{marginBottom:8}}>Appliances</div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:8}}>
                  {[['appliance_fridge','Fridge'],['appliance_stove','Stove'],['appliance_dishwasher','Dishwasher'],
                    ['appliance_microwave','Microwave'],['appliance_disposal','Disposal']].map(([k,lbl]) => (
                    <Field key={k} label={lbl}>
                      <SELECT value={data[k]} onChange={v => save(k, v)} options={APPLIANCE_3} />
                    </Field>
                  ))}
                </div>
              </div>
              <div style={{gridColumn:'span 2'}}>
                <CheckBox label="Under-cabinet lighting" checked={data.under_cabinet_lighting}
                  onChange={v => save('under_cabinet_lighting', v)} />
              </div>
            </>)}

            {/* ── Bathroom extras ── */}
            {isBath && (<>
              <div style={{gridColumn:'span 2',marginBottom:4,marginTop:8}}><div className="section-title">Bathroom</div></div>
              <Field label="Toilet">
                <SELECT value={data.toilet_condition} onChange={v => save('toilet_condition', v)} options={CONDITION_2} />
              </Field>
              <Field label="Vanity">
                <SELECT value={data.vanity_condition} onChange={v => save('vanity_condition', v)}
                  options={[['keep','Keep'],['paint','Paint'],['replace','Replace']]} />
              </Field>
              <Field label="Vanity Size">
                <SELECT value={data.vanity_size} onChange={v => save('vanity_size', v)}
                  options={[['single','Single'],['double','Double']]} />
              </Field>
              <Field label="Vanity Countertop">
                <SELECT value={data.vanity_countertop_type} onChange={v => save('vanity_countertop_type', v)}
                  options={[['','— Select —'],['cultured_marble','Cultured Marble'],['laminate','Laminate'],
                    ['granite','Granite'],['quartz','Quartz'],['solid_surface','Solid Surface'],
                    ['tile','Tile'],['other','Other']]} />
              </Field>
              <Field label="Tub / Shower">
                <SELECT value={data.tub_shower_condition} onChange={v => save('tub_shower_condition', v)}
                  options={[['keep','Keep'],['retile','Retile'],['replace','Replace']]} />
              </Field>
              <Field label="Tile Sq Ft">
                <NumberInput value={data.tile_sq_ft} onChange={v => save('tile_sq_ft', v)} />
              </Field>
              <Field label="Exhaust Fan">
                <SELECT value={data.exhaust_fan_condition} onChange={v => save('exhaust_fan_condition', v)} options={CONDITION_2} />
              </Field>
              <Field label="Mirror">
                <SELECT value={data.mirror_condition} onChange={v => save('mirror_condition', v)} options={CONDITION_2} />
              </Field>
            </>)}

            {/* ── Notes ── */}
            <div style={{gridColumn:'span 2',marginTop:8}}>
              <Field label="Notes">
                <textarea rows={2} defaultValue={data.notes || ''}
                  onBlur={e => save('notes', e.target.value)}
                  style={{resize:'vertical'}} placeholder="Anything else worth noting…" />
              </Field>
            </div>

          </div>

          <SOWCostTable dealId={dealId} lines={costLines} sowCosts={sowCosts} onSynced={onUpdate} />
        </div>
      )}
    </div>
  );
}
